import React from 'react';
import {
	Box,
	Button,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import District from '../../model/District';

export type Stage3JuryPairDraft = {
	id: number | null;
	name: string;
	juror1_name: string;
	juror1_email: string;
	juror2_name: string;
	juror2_email: string;
	juror1_district_id: number | '' | null;
	juror2_district_id: number | '' | null;
	drive_folder_url: string;
	notes: string;
};

type Props = {
	districts: District[];
	draft: Stage3JuryPairDraft;
	onChange: (next: Stage3JuryPairDraft) => void;
	onSave: () => void;
	onReset: () => void;
	isSaving: boolean;
	currentPairVerified: boolean;
	currentPairVerifyError: string;
};

const Stage3JuryPairForm = ({
	districts,
	draft,
	onChange,
	onSave,
	onReset,
	isSaving,
	currentPairVerified,
	currentPairVerifyError,
}: Props) => (
	<Box sx={{ mt: 2 }}>
		<Typography variant='subtitle1'>{draft.id ? 'Edycja komisji' : 'Nowa komisja'}</Typography>
		<Grid
			container
			spacing={1}
			sx={{ mt: 0.5 }}
		>
			<Grid
				item
				xs={12}
			>
				<TextField
					fullWidth
					label='Nazwa komisji'
					value={draft.name}
					onChange={(e) => onChange({ ...draft, name: e.target.value })}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				<TextField
					fullWidth
					label='Juror 1 (email do udostępnienia)'
					value={draft.juror1_email}
					onChange={(e) => onChange({ ...draft, juror1_email: e.target.value })}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				<TextField
					fullWidth
					label='Juror 1 (nazwa)'
					value={draft.juror1_name}
					onChange={(e) => onChange({ ...draft, juror1_name: e.target.value })}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				<TextField
					fullWidth
					label='Juror 2 (nazwa)'
					value={draft.juror2_name}
					onChange={(e) => onChange({ ...draft, juror2_name: e.target.value })}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				<TextField
					fullWidth
					label='Juror 2 (email do udostępnienia)'
					value={draft.juror2_email}
					onChange={(e) => onChange({ ...draft, juror2_email: e.target.value })}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				<FormControl fullWidth>
					<InputLabel id='jury-pair-j1-district'>Okręg jurora 1</InputLabel>
					<Select
						labelId='jury-pair-j1-district'
						label='Okręg jurora 1'
						value={draft.juror1_district_id}
						onChange={(e) =>
							onChange({ ...draft, juror1_district_id: e.target.value === '' ? '' : Number(e.target.value) })
						}
					>
						<MenuItem value=''>Dowolny okręg</MenuItem>
						{districts.map((district) => (
							<MenuItem
								key={district.id}
								value={district.id}
							>
								{district.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				<FormControl fullWidth>
					<InputLabel id='jury-pair-j2-district'>Okręg jurora 2</InputLabel>
					<Select
						labelId='jury-pair-j2-district'
						label='Okręg jurora 2'
						value={draft.juror2_district_id}
						onChange={(e) =>
							onChange({ ...draft, juror2_district_id: e.target.value === '' ? '' : Number(e.target.value) })
						}
					>
						<MenuItem value=''>Dowolny okręg</MenuItem>
						{districts.map((district) => (
							<MenuItem
								key={district.id}
								value={district.id}
							>
								{district.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid
				item
				xs={12}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<TextField
						fullWidth
						label='Link do folderu Google Drive'
						value={draft.drive_folder_url}
						onChange={(e) => onChange({ ...draft, drive_folder_url: e.target.value })}
					/>
					{currentPairVerified && (
						<Tooltip title='Folder zweryfikowany'>
							<CheckCircleIcon
								color='success'
								fontSize='small'
							/>
						</Tooltip>
					)}
				</Box>
				{!currentPairVerified && currentPairVerifyError && (
					<Typography
						variant='caption'
						color='error.main'
					>
						{currentPairVerifyError}
					</Typography>
				)}
			</Grid>
			<Grid
				item
				xs={12}
			>
				<TextField
					fullWidth
					multiline
					minRows={2}
					label='Notatki'
					value={draft.notes}
					onChange={(e) => onChange({ ...draft, notes: e.target.value })}
				/>
			</Grid>
		</Grid>
		<Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
			<Button
				variant='contained'
				onClick={onSave}
				disabled={isSaving}
			>
				{isSaving ? 'Zapisywanie...' : draft.id ? 'Zapisz zmiany' : 'Dodaj parę'}
			</Button>
			<Button
				variant='outlined'
				onClick={onReset}
				disabled={isSaving}
			>
				Wyczyść
			</Button>
		</Box>
	</Box>
);

export default Stage3JuryPairForm;
